// Derived strings the preview documents share. Keeping them here means the
// invoice and the meal plan can never disagree about how a name or an address
// is assembled from the form.

import { BrandDetails } from "./types/brand.types";

/** The name the documents are signed with, as the form collects it. */
export const senderName = (details: BrandDetails) => details.fullName.trim();

/** The second address line: city, state and pincode, in that order. */
export const senderAddressLine = (details: BrandDetails) =>
  [details.city, details.state, details.pincode]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(", ");

/**
 * The rows of the bank block. The payment QR is deliberately not one of them:
 * it is an image beside the block rather than a line in it, and uploading one
 * says nothing about whether the account details have been filled in.
 */
const PAYMENT_FIELDS = [
  "accountHolder",
  "accountNumber",
  "bankName",
  "ifsc",
  "upiNumber",
  "upiId",
] as const;

/**
 * Whether any payment detail has been given. The block stands or falls
 * together: once one row is the dietitian's own, the stand-ins are withdrawn
 * from the rest rather than sitting alongside it — an invoice that reads as
 * complete while half of it is specimen data is worse than a visibly
 * unfinished one, because only the first kind gets sent to a client.
 */
export const hasPaymentDetails = (details: BrandDetails) =>
  PAYMENT_FIELDS.some((field) => details[field].trim() !== "");

/** Public page slug — the brand name if there is one, else the person's. */
export const brandHandle = (details: BrandDetails) => {
  const source = details.brandName.trim() || details.fullName.trim();
  return source.toLowerCase().replace(/\s+/g, "-");
};
